import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import DateRangeField from "../Inputs/DateRangeField.jsx";
import FileField from "../Inputs/FileField.jsx";
import NewDate from "../Inputs/NewDate.jsx";

import { Tooltip } from "@mui/material";
import { InfoCircle } from "react-bootstrap-icons";
import RadioGroupField from "../Inputs/RadioGroupField";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper.js";
import * as Messages from "../messages/messages";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";
import "./styles/project-information.css";

// Slide 3: Project Information
const ProjectInformation = ({
  setSlideStatus,
  slideState,
  formStateLoaded,
}) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const [open, setOpen] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const {
    slide1,
    slide11,
    setSlide11,
    slide8,
    setSlide8,
    setIsLoading,
    requestStatus,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
    handleInputDisable,
  } = useContext(FormContext);

  const { clientId, projectId, userId } = useContext(DashboardContext);

  // On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide8);
  }, []);

  // Update Slide State
  const slide8Mutation = useSetSlide(
    clientId,
    projectId,
    8,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "8",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <h2 className="question-section">Duration of Construction</h2>
          <ResponsiveRow type="center">
            <NewDate
              idProp="startDate"
              label="Estimated Start Date"
              state={slide8}
              setState={setSlide8}
              val={slide8.startDate}
              debouncedSave={slide8Mutation}
              setIsLoading={setIsLoading}
              req={
                slide8.startDate === "" ||
                (slide8.endDate !== "" &&
                  dayjs(slide8.startDate).isAfter(slide8.endDate))
              }
              disabledInput={disabledInputCheck("startDate", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("startDate", disabledInputs) === false
                  ? getInputMessage("startDate", invalidInputsData, "8")
                  : ""
              }
              invalidData={getInvalidData("startDate", invalidInputsData, "8")}
            />
            <NewDate
              idProp="endDate"
              label="Estimated End Date"
              state={slide8}
              setState={setSlide8}
              val={slide8.endDate}
              debouncedSave={slide8Mutation}
              setIsLoading={setIsLoading}
              req={
                slide8.endDate === "" ||
                dayjs(slide8.endDate).isBefore(slide8.startDate)
              }
              disabledInput={disabledInputCheck("endDate", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("endDate", disabledInputs) === false
                  ? getInputMessage("endDate", invalidInputsData, "8")
                  : ""
              }
              invalidData={getInvalidData("endDate", invalidInputsData, "8")}
            />
          </ResponsiveRow>
          <h2 className="question-section">Major Activity Schedule</h2>
          <p className="important-text">
            Please enter the estimated start and completion dates for the major
            activities that will be performed on this project
          </p>
          {slide1.projectType !== "residential" ? (
            <>
              <ResponsiveRow type="default">
                <DateRangeField
                  idProp="stockpile"
                  label="Stockpile"
                  val={[
                    dayjs(slide8.stockpileStart) || null,
                    dayjs(slide8.stockpileComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "stockpileStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("stockpileStart", disabledInputs) ===
                    false
                      ? getInputMessage(
                          "stockpileStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "stockpileStart",
                    invalidInputsData,
                    "8",
                  )}
                />
                <DateRangeField
                  idProp="paving"
                  label="Paving"
                  val={[
                    dayjs(slide8.pavingStart) || null,
                    dayjs(slide8.pavingComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "pavingStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("pavingStart", disabledInputs) === false
                      ? getInputMessage("pavingStart", invalidInputsData, "8")
                      : ""
                  }
                  invalidData={getInvalidData(
                    "pavingStart",
                    invalidInputsData,
                    "8",
                  )}
                />
              </ResponsiveRow>
              <ResponsiveRow type="default">
                <DateRangeField
                  idProp="clearingGrubbing"
                  label="Clearing & Grubbing"
                  val={[
                    dayjs(slide8.clearingGrubbingStart) || null,
                    dayjs(slide8.clearingGrubbingComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "clearingGrubbingStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck(
                      "clearingGrubbingStart",
                      disabledInputs,
                    ) === false
                      ? getInputMessage(
                          "clearingGrubbingStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "clearingGrubbingStart",
                    invalidInputsData,
                    "8",
                  )}
                />
                <DateRangeField
                  idProp="vertical"
                  label="Vertical Construction"
                  val={[
                    dayjs(slide8.verticalStart) || null,
                    dayjs(slide8.verticalComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "verticalStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("verticalStart", disabledInputs) ===
                    false
                      ? getInputMessage("verticalStart", invalidInputsData, "8")
                      : ""
                  }
                  invalidData={getInvalidData(
                    "verticalStart",
                    invalidInputsData,
                    "8",
                  )}
                />
              </ResponsiveRow>
              <ResponsiveRow type="default">
                <DateRangeField
                  idProp="grading"
                  label="Grading"
                  val={[
                    dayjs(slide8.gradingStart) || null,
                    dayjs(slide8.gradingComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "gradingStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("gradingStart", disabledInputs) === false
                      ? getInputMessage("gradingStart", invalidInputsData, "8")
                      : ""
                  }
                  invalidData={getInvalidData(
                    "gradingStart",
                    invalidInputsData,
                    "8",
                  )}
                />

                <DateRangeField
                  idProp="landscaping"
                  label="Landscaping"
                  val={[
                    dayjs(slide8.landscapingStart) || null,
                    dayjs(slide8.landscapingComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "landscapingStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("landscapingStart", disabledInputs) ===
                    false
                      ? getInputMessage(
                          "landscapingStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "landscapingStart",
                    invalidInputsData,
                    "8",
                  )}
                />
              </ResponsiveRow>
              <ResponsiveRow type="default">
                <DateRangeField
                  idProp="infrastructure"
                  label="Infrastructure"
                  val={[
                    dayjs(slide8.infrastructureStart) || null,
                    dayjs(slide8.infrastructureComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "infrastructureStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck(
                      "infrastructureStart",
                      disabledInputs,
                    ) === false
                      ? getInputMessage(
                          "infrastructureStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "infrastructureStart",
                    invalidInputsData,
                    "8",
                  )}
                />
                <DateRangeField
                  idProp="finalStabilization"
                  label="Final Stabilization"
                  val={[
                    dayjs(slide8.finalStabilizationStart) || null,
                    dayjs(slide8.finalStabilizationComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "finalStabilizationStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck(
                      "finalStabilizationStart",
                      disabledInputs,
                    ) === false
                      ? getInputMessage(
                          "finalStabilizationStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "finalStabilizationStart",
                    invalidInputsData,
                    "8",
                  )}
                />
              </ResponsiveRow>
            </>
          ) : (
            <>
              <ResponsiveRow type="default">
                <DateRangeField
                  idProp="vertical"
                  label="Vertical Construction"
                  val={[
                    dayjs(slide8.verticalStart) || null,
                    dayjs(slide8.verticalComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "verticalStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("verticalStart", disabledInputs) ===
                    false
                      ? getInputMessage("verticalStart", invalidInputsData, "8")
                      : ""
                  }
                  invalidData={getInvalidData(
                    "verticalStart",
                    invalidInputsData,
                    "8",
                  )}
                />
                <DateRangeField
                  idProp={"landscaping"}
                  label={"Landscaping"}
                  val={[
                    dayjs(slide8.landscapingStart) || null,
                    dayjs(slide8.landscapingComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "landscapingStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("landscapingStart", disabledInputs) ===
                    false
                      ? getInputMessage(
                          "landscapingStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "landscapingStart",
                    invalidInputsData,
                    "8",
                  )}
                />
              </ResponsiveRow>
              <ResponsiveRow type="default">
                <DateRangeField
                  idProp={"finalStabilization"}
                  label={"Final Stabilization"}
                  val={[
                    dayjs(slide8.finalStabilizationStart) || null,
                    dayjs(slide8.finalStabilizationComplete) || null,
                  ]}
                  state={slide8}
                  setState={setSlide8}
                  debouncedSave={slide8Mutation}
                  setIsLoading={setIsLoading}
                  disabledInput={disabledInputCheck(
                    "finalStabilizationStart",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck(
                      "finalStabilizationStart",
                      disabledInputs,
                    ) === false
                      ? getInputMessage(
                          "finalStabilizationStart",
                          invalidInputsData,
                          "8",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "finalStabilizationStart",
                    invalidInputsData,
                    "8",
                  )}
                />
              </ResponsiveRow>
            </>
          )}
          <div id="activities-description-header">
            <h2 className="question-section">Activities Description</h2>
            <Tooltip
              id="tf-tooltip"
              classes={{
                tooltip: "text-field-tooltip",
                popper: "text-field-popper",
                arrow: "text-field-arrow",
                tooltipPlacementLeft: "text-field-right",
              }}
              title={Messages[`activitiesDescriptionMessage`] || ""}
              arrow
              placement={"right"}
            >
              <InfoCircle
                className="text-info-circ"
                color="white"
                size={30}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
              />
            </Tooltip>
          </div>
          <p className="important-text">
            Please enter a description of the activities that will be performed
            on this project
          </p>
          <textarea
            className={`form-textarea ${!slide8.activitiesDescription || slide8.activitiesDescription.length < 10 ? "required" : ""}`}
            id="activities-description-textarea"
            value={slide8.activitiesDescription || ""}
            disabled={
              handleInputDisable(
                "activitiesDescription",
                slide8,
                disabledInputCheck("activitiesDescription", disabledInputs),
                userId,
              ) === true
                ? true
                : false
            }
            placeholder="Land Development & Residential construction activities including clearing & grubbing, mass grading, trenching, utility installation, concrete work, street improvements, landscaping, community pools, parks, and other related activities."
            onChange={(e) => {
              let newState = {
                ...slide8,
                activitiesDescription: e.target.value,
              };
              setSlide8(newState);
              setIsLoading(true);
              slide8Mutation.mutate(newState);
            }}
            rows={4}
          />
          <h2 className="question-section">Offsites</h2>
          <RadioGroupField
            idProp="offsites"
            label="Will there be offsites?"
            val={slide8.offsites}
            state={slide8}
            setState={setSlide8}
            debouncedSave={slide8Mutation}
            setIsLoading={setIsLoading}
            disabledInput={disabledInputCheck("offsites", disabledInputs)}
          />
          {slide8.offsites !== "No" ? (
            <>
              <h2 className="question-section">Offsites Description</h2>
              <p className="important-text">
                Please add a description detailing the activities to be
                performed on the offsite areas
              </p>
              <textarea
                className={`form-textarea ${slide8.offsites === "Yes" && !slide8.offsiteDescription ? "required" : ""}`}
                id="offsite-textarea"
                value={slide8.offsiteDescription}
                onChange={(e) => {
                  let newState = {
                    ...slide8,
                    offsiteDescription: e.target.value,
                  };
                  setSlide8(newState);

                  setIsLoading(true);
                  slide8Mutation.mutate(newState);
                }}
                rows={4}
              />
              <FileField
                label={"Offsite Map"}
                idProp={"offsiteMap"}
                state={slide11}
                setState={setSlide11}
                disabledInput={disabledInputCheck("offsiteMap", disabledInputs)}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
              />
            </>
          ) : null}
          <FileField
            idProp={"gradingPlan"}
            label={"Grading Plan"}
            state={slide11}
            setState={setSlide11}
            disabledInput={disabledInputCheck("gradingPlan", disabledInputs)}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
          />
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default ProjectInformation;
